/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { DateTime } from 'luxon';
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';

import { Box, CircularProgress } from '@mui/material';

import { HURoute } from '../../components/HURoute/HURoute';
import { adminRoutes } from '../../constants/adminRoutes';
import { BasicRoutes } from '../../constants/basicRoutes';
import { AdminLayout } from '../../layouts/AdminLayout/AdminLayout';
import { NotFoundLayout } from '../../layouts/NotFoundLayout/NotFoundLayout';
import { AdminDatasetsCategoriesPage } from '../../pages/AdminDatasetsCategoriesPage/AdminDatasetsCategoriesPage';
import { AdminDatasetsPage } from '../../pages/AdminDatasetsPage/AdminDatasetsPage';
import { AdminJiraTicketsPage } from '../../pages/AdminJiraTicketsPage/AdminJiraTicketsPage';
import { AdminManageComputePlansPage } from '../../pages/AdminManageComputePlansPage/AdminManageComputePlansPage';
import { AdminManageStoragePlansPage } from '../../pages/AdminManageStoragePlansPage/AdminManageStoragePlansPage';
import { AdminManageSupportPlansPage } from '../../pages/AdminManageSupportPlans/AdminManageSupportPlansPage';
import { AdminOrganizationsPage } from '../../pages/AdminOrganizationsPage/AdminOrganizationsPage';
import { AdminOrphansPage } from '../../pages/AdminOrphansPage/AdminOrphansPage';
import { AdminUsersPage } from '../../pages/AdminUsersPage/AdminUsersPage';
import { EmailConfigurationPage } from '../../pages/EmailConfigurationPage/EmailConfigurationPage';
import { ImprintConfigurationPage } from '../../pages/ImprintConfigurationPage/ImprintConfigurationPage';
import { NotFoundPage } from '../../pages/NotFoundPage/NotFoundPage';
import { SupportConfigurationPage } from '../../pages/SupportConfigurationPage/SupportConfigurationPage';
import { SystemConfigurationPage } from '../../pages/SystemConfigurationPage/SystemConfigurationPage';
import { RootState } from '../../store';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

const AdminSectionRoutesComponent = memo<PropsFromRedux>(props => {
  const {
    auth: { isAuthCheckCompleted, user },
  } = props;
  const { i18n } = useTranslation();

  useEffect(() => {
    DateTime.now().setLocale(i18n.language);
  }, [i18n.language]);

  if (!isAuthCheckCompleted) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
        <CircularProgress color="primary" size={60} />
      </Box>
    );
  }

  if (isAuthCheckCompleted && !user?.isSuperuser) {
    return <Navigate to={BasicRoutes.HOME} />;
  }

  return (
    <Routes>
      <Route
        index
        element={
          <HURoute privateRoute>
            <Navigate to={{ pathname: adminRoutes.ADMIN_ENQUIRIES }} replace relative="route" />
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_USERS}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminUsersPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_ENQUIRIES}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminJiraTicketsPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_DATASETS}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminDatasetsPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_DATASETS_CATEGORIES}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminDatasetsCategoriesPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_SYSTEM_CONFIG}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <SystemConfigurationPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_EMAIL_CONFIG}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <EmailConfigurationPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_SUPPORT_CONFIG}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <SupportConfigurationPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_IMPRINT_CONFIG}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <ImprintConfigurationPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_ORGANIZATIONS}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminOrganizationsPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_CLUSTERS_ORPHANS}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminOrphansPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_MANAGE_PLANS_STORAGE}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminManageStoragePlansPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_MANAGE_PLANS_COMPUTE}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminManageComputePlansPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path={adminRoutes.ADMIN_MANAGE_PLANS_SUPPORT}
        element={
          <HURoute privateRoute>
            <AdminLayout>
              <AdminManageSupportPlansPage />
            </AdminLayout>
          </HURoute>
        }
      />
      <Route
        path="*"
        element={
          <HURoute privateRoute>
            <NotFoundLayout>
              <NotFoundPage redirectLink={BasicRoutes.ADMIN} />
            </NotFoundLayout>
          </HURoute>
        }
      />
    </Routes>
  );
});

export const AdminSectionRoutes = connector(AdminSectionRoutesComponent);
